/**
 * Single source of truth for tuning values shared by client and server.
 * Anything that affects the simulation lives here so both sides agree.
 */
import type { Aabb, Vec2 } from "../math/vec2.js";

/** Authoritative server simulation rate (Hz). 20 Hz = 50 ms per tick. */
export const TICK_RATE = 20;
export const TICK_DURATION_MS = 1000 / TICK_RATE;
/** Fixed timestep in seconds, used by every sim integration step. */
export const FIXED_DT = 1 / TICK_RATE;

/** Square arena, half-extent in world units (meters). Playfield = 2 * half. */
export const ARENA_HALF_EXTENT = 12;

/** Network: how many past inputs the client keeps for reconciliation replay. */
export const INPUT_HISTORY_SIZE = 128;

// ---------------------------------------------------------------------------
// Characters (data-driven). Each entity references a CharacterId; the sim reads
// all of its stats from here, so adding a brawler is pure data.
// ---------------------------------------------------------------------------

export type CharacterId = "striker" | "scatter";

export interface ProjectileDef {
  /** Travel speed in units/second. */
  speed: number;
  damage: number;
  /** Collision + visual radius. */
  radius: number;
  /** Lifetime in ticks before despawn (range = speed * ttl * FIXED_DT). */
  ttlTicks: number;
}

export interface WeaponDef {
  /** Ticks between shots. */
  cooldownTicks: number;
  /** Projectiles per shot (1 = single). */
  pellets: number;
  /** Total spread cone in radians (0 for a single accurate shot). */
  spreadRad: number;
  projectile: ProjectileDef;
}

export interface CharacterDef {
  id: CharacterId;
  name: string;
  /** Movement speed in units/second. */
  moveSpeed: number;
  /** Collision/selection radius in units. */
  radius: number;
  maxHealth: number;
  weapon: WeaponDef;
}

export const CHARACTERS: Record<CharacterId, CharacterDef> = {
  striker: {
    id: "striker",
    name: "Striker",
    moveSpeed: 5,
    radius: 0.5,
    maxHealth: 100,
    weapon: {
      cooldownTicks: 7,
      pellets: 1,
      spreadRad: 0,
      projectile: { speed: 14, damage: 20, radius: 0.22, ttlTicks: 60 },
    },
  },
  scatter: {
    id: "scatter",
    name: "Scatter",
    moveSpeed: 4.4,
    radius: 0.5,
    maxHealth: 120,
    weapon: {
      cooldownTicks: 11,
      pellets: 3,
      spreadRad: 0.42,
      projectile: { speed: 12, damage: 13, radius: 0.22, ttlTicks: 26 },
    },
  },
};

export const DEFAULT_CHARACTER: CharacterId = "striker";
export const DEFAULT_BOT_CHARACTER: CharacterId = "scatter";

/** Derived effective weapon range (units) of a character. */
export const weaponRange = (c: CharacterDef): number =>
  c.weapon.projectile.speed * c.weapon.projectile.ttlTicks * FIXED_DT;

// ---------------------------------------------------------------------------
// Bot AI tuning.
// ---------------------------------------------------------------------------

export const BOT_AI = {
  sightRange: 18,
  /** Preferred distance band — kite to stay inside it. */
  bandMin: 6,
  bandMax: 9.5,
  /** Lateral (strafe) weight blended into movement. */
  strafeBias: 0.7,
  /** How far ahead (units) to test for cover when steering. */
  avoidLookahead: 2.6,
  /** Residual aim error (radians) so the bot is beatable. */
  aimErrorRad: 0.1,
  /** Retreat toward cover below this health fraction. */
  lowHealthFrac: 0.3,
  /** Flip strafe direction every N ticks. */
  strafeFlipTicks: 28,
  /** An enemy projectile within this distance counts as an active threat. */
  threatDist: 4,
} as const;

// ---------------------------------------------------------------------------
// Health pickups.
// ---------------------------------------------------------------------------

export const PICKUP = {
  heal: 40,
  respawnTicks: 200,
  radius: 0.6,
} as const;

// ---------------------------------------------------------------------------
// Arenas. The active arena's obstacles drive both sim collision and rendering.
// ---------------------------------------------------------------------------

export type ArenaId = "default" | "arena2";

export interface ArenaDef {
  id: ArenaId;
  obstacles: readonly Aabb[];
  pickups: readonly Vec2[];
  spawns: { player: Vec2; bot: Vec2 };
}

export const ARENAS: Record<ArenaId, ArenaDef> = {
  // The original sparse layout — kept for the cover unit test.
  default: {
    id: "default",
    obstacles: [
      { min: { x: -7.5, y: -2 }, max: { x: -5.5, y: 2 } },
      { min: { x: 5.5, y: -2 }, max: { x: 7.5, y: 2 } },
      { min: { x: -1.5, y: 4.5 }, max: { x: 1.5, y: 6.5 } },
      { min: { x: -1.5, y: -6.5 }, max: { x: 1.5, y: -4.5 } },
    ],
    pickups: [{ x: 0, y: 0 }],
    spawns: { player: { x: 0, y: -8 }, bot: { x: 0, y: 8 } },
  },
  // Richer, symmetric layout with clear lanes for the bot to navigate.
  arena2: {
    id: "arena2",
    obstacles: [
      { min: { x: -1.6, y: -1.6 }, max: { x: 1.6, y: 1.6 } }, // center block
      { min: { x: -9, y: 3 }, max: { x: -6, y: 5 } }, // upper-left
      { min: { x: 6, y: 3 }, max: { x: 9, y: 5 } }, // upper-right
      { min: { x: -9, y: -5 }, max: { x: -6, y: -3 } }, // lower-left
      { min: { x: 6, y: -5 }, max: { x: 9, y: -3 } }, // lower-right
      { min: { x: -1, y: 7 }, max: { x: 1, y: 9 } }, // top pillar
      { min: { x: -1, y: -9 }, max: { x: 1, y: -7 } }, // bottom pillar
    ],
    pickups: [
      { x: -8, y: 0 },
      { x: 8, y: 0 },
    ],
    spawns: { player: { x: 0, y: -10 }, bot: { x: 0, y: 10 } },
  },
};

export const DEFAULT_ARENA: ArenaId = "arena2";
